import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit, Plus, Trash2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

import ImageCropperModal from './ImageCropperModal';
import { Crop } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MotoTunersTab({ tuners, onRefresh, onDelete }) {
  const [isOpen, setIsOpen] = useState(false);
  const [editingTuner, setEditingTuner] = useState(null);
  const [formData, setFormData] = useState({ name: '', price: 30000, image_url: '', description: '', history: '' });
  const [uploading, setUploading] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      setUploading(true);
      toast.loading('Uploading image...');
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.dismiss();
      
      const imageUrl = response.data.url;
      setCropperImage(imageUrl);
      toast.success('Image uploaded! Crop it now or close to use as-is.');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTuner) {
        await axios.put(`${BACKEND_URL}/api/moto/tuners/${editingTuner.tuner_id}`, formData, { withCredentials: true });
        toast.success('Moto Tuner updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/moto/tuners`, formData, { withCredentials: true });
        toast.success('Moto Tuner created');
      }
      setIsOpen(false);
      setEditingTuner(null);
      setFormData({ name: '', price: 30000, image_url: '', description: '', history: '' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to save MotoMSF tuner');
    }
  };

  const openEdit = (tuner) => {
    setEditingTuner(tuner);
    setFormData({ name: tuner.name, price: tuner.price || 30000, image_url: tuner.image_url || '', description: tuner.description || '', history: tuner.history || '' });
    setIsOpen(true);
  };

  return (
    <>
    <div className="bg-card border border-zinc-800 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading font-bold text-2xl">Moto <span className="text-secondary">Tuners</span> Management</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="add-moto-tuner-button"
              onClick={() => { setEditingTuner(null); setFormData({ name: '', price: 30000, image_url: '', description: '', history: '' }); }}
              className="btn-skew bg-secondary text-black px-6 py-2 font-bold uppercase flex items-center space-x-2"
            >
              <Plus size={18} />
              <span>Add Moto Tuner</span>
            </button>
          </DialogTrigger>
          <DialogContent className="bg-card border border-zinc-800 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-2xl">
                {editingTuner ? 'Edit Moto Tuner' : 'Add Moto Tuner'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Name</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary h-12 px-4 text-white" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Price (Credits: 20K-50K)</label>
                <input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary h-12 px-4 text-white" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Image</label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input type="text" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="Enter URL or upload file" className="flex-1 bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary h-12 px-4 text-white" />
                    <label className={`flex items-center gap-2 px-4 h-12 cursor-pointer transition-colors ${uploading ? 'bg-zinc-700' : 'bg-secondary hover:bg-secondary/80'} text-black`}>
                      <Upload size={18} />
                      <span>{uploading ? 'Uploading...' : 'Upload'}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} />
                    </label>
                  </div>
                  {formData.image_url && (
                    <div className="flex items-center gap-2">
                      <img src={formData.image_url} alt="Preview" className="w-16 h-16 object-cover border border-zinc-700" />
                      <button type="button" onClick={() => setCropperImage(formData.image_url)} className="flex items-center gap-1 text-primary hover:text-primary/80 text-sm"><Crop size={14} /> Crop</button>
                      <button type="button" onClick={() => setFormData({ ...formData, image_url: '' })} className="text-red-500 hover:text-red-400 text-sm">Remove</button>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Description (optional)</label>
                <textarea data-testid="moto-tuner-description-input" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary px-4 py-3 text-white min-h-[80px]" placeholder="Brief description about the Moto tuner team..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">History (optional)</label>
                <textarea data-testid="moto-tuner-history-input" value={formData.history} onChange={(e) => setFormData({ ...formData, history: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 focus:border-secondary focus:ring-1 focus:ring-secondary px-4 py-3 text-white min-h-[80px]" placeholder="Team history, championships, notable achievements..." />
              </div>
              <button type="submit" className="w-full bg-secondary text-black py-3 font-bold uppercase">
                {editingTuner ? 'Update' : 'Create'}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">NAME</th>
              <th className="text-left p-4 font-heading font-bold text-sm text-zinc-400">PRICE</th>
              <th className="text-right p-4 font-heading font-bold text-sm text-zinc-400">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {tuners.map((tuner) => (
              <tr key={tuner.tuner_id} className="border-b border-zinc-800 hover:bg-zinc-900">
                <td className="p-4 font-bold">{tuner.name}</td>
                <td className="p-4 text-secondary font-mono">{(tuner.price || 0).toLocaleString()} CR</td>
                <td className="p-4 text-right">
                  <button onClick={() => openEdit(tuner)} className="text-secondary hover:text-white mr-3">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => onDelete(tuner.tuner_id)} className="text-red-500 hover:text-red-400">
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

      {cropperImage && (
        <ImageCropperModal
          imageSrc={cropperImage}
          onCropped={async (url) => {
            setFormData(prev => ({ ...prev, image_url: url }));
            setCropperImage(null);
            if (editingTuner) {
              try {
                await axios.put(`${BACKEND_URL}/api/moto/tuners/${editingTuner.tuner_id}`, { ...formData, image_url: url }, { withCredentials: true });
                toast.success('Moto Tuner photo updated!');
                onRefresh();
              } catch { toast.error('Photo cropped but failed to save tuner'); }
            }
          }}
          onClose={() => { setFormData(prev => prev.image_url ? prev : { ...prev, image_url: cropperImage }); setCropperImage(null); }}
        />
      )}
    </>
  );
}